import { Dispatch, SetStateAction, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, useTheme } from '@mui/material';
import { ComplianceStatus } from '../../../../types';
import CompliancePlip from '../../../../components/CompliancePlip';
import { Autoclave, OpenSporetestModal, SporetestingResult } from '../types';
import { standardDateFormat } from '../../../../helpers/date';
import { updateAutoclave, updateSporeTest } from '../../../../services/biologicalServices';
import { addError, addSuccess } from '../../../../redux/alerts';
import { useAppDispatch } from '../../../../redux/store';
import useCustomerNumber from '../../../../hooks/useCustomerNumber';

interface UnassignedSporetestProps {
  autoclaves: Autoclave[];
  reload: () => Promise<void>;
  unassignedTestResults: SporetestingResult[];
  setOpenSporetestModal: Dispatch<SetStateAction<OpenSporetestModal>>;
}

const UnassignedSporetest: React.FC<UnassignedSporetestProps> = ({ autoclaves, reload, unassignedTestResults, setOpenSporetestModal }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const customerNumber = useCustomerNumber()
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedAutoclave, setSelectedAutoclave] = useState<string>('');
  const [selectedSporetestIds, setSelectedSporetestIds] = useState<Set<number>>(new Set());

  const allCrosstexIds = unassignedTestResults.map((item) => Number(item.test_result_id));
  const isAllSelected = selectedSporetestIds.size === unassignedTestResults.length;

  const handleToggle = (id: number) => {
    setSelectedSporetestIds((prev) => {
      const updated = new Set(prev);
      if (updated.has(id)) {
        updated.delete(id);
      } else {
        updated.add(id);
      }
      return updated;
    });
  };

  const handleOpenSporeTestModal = (type: string, sporetest: SporetestingResult | null) => {
    setOpenSporetestModal({
      open: true,
      autoclaveId: null,
      testType: type,
      sporetest,
    })
  };

  const handleSelectAll = () => {
    setSelectedSporetestIds(isAllSelected ? new Set() : new Set(allCrosstexIds));
  };

  const handleSubmit = async () => {
    const selectedItems = unassignedTestResults
      .filter((test) => selectedSporetestIds.has(Number(test.test_result_id)))
      .map((test) => ({ ...test, autoclave_id: Number(selectedAutoclave) }));

    try {
      setLoading(true);
      await Promise.all(selectedItems.map((sporetest) => updateSporeTest(customerNumber, sporetest)));

      const autoclaveToUpdate = autoclaves.find(autoclave => Number(autoclave.autoclave_id) === Number(selectedAutoclave));

      if (autoclaveToUpdate) {
        const { active, customer_number, autoclave_id, autoclave_name, autoclavetype_id, autoclave_brand, autoclave_model, autoclave_serial, autoclave_frequency } = autoclaveToUpdate
        const autoclavePayload = {
          customer_number,
          autoclave_id,
          autoclave_name,
          autoclavetype_id,
          autoclave_brand,
          autoclave_model,
          autoclave_serial,
          autoclave_frequency,
          active,
        }

        await updateAutoclave(customer_number, autoclavePayload);
      }

      setLoading(false);
    } catch (error) {
      console.error(error);
      dispatch(addError(error instanceof Error ? error.message : 'There was a problem assigning the sporetests'));
      setLoading(false);
      return;
    }

    dispatch(addSuccess(`The sporetest${selectedSporetestIds.size > 1 ? 's' : ''} have been reassigned`));
    setSelectedAutoclave('');
    setSelectedSporetestIds(new Set());
    reload();
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
        <FormControl sx={{ minWidth: '250px' }}>
          <InputLabel shrink={true} htmlFor="grouped-native-select">
            Assign To
          </InputLabel>
          <Select
            displayEmpty
            sx={{ minWidth: 130 }}
            label="Assign To"
            value={selectedAutoclave}
            onChange={(e) => setSelectedAutoclave(e.target.value)}
            size="small"
          >
            <MenuItem value="">Select an autoclave</MenuItem>
            {autoclaves
              .sort((a, b) => a.autoclave_name.localeCompare(b.autoclave_name))
              .map((autoclave) => (
                <MenuItem key={autoclave.autoclave_id} value={autoclave.autoclave_id}>
                  {autoclave.autoclave_name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: 1, pb: 2 }}>
        <FormControlLabel control={<Checkbox checked={isAllSelected} onChange={handleSelectAll} />} label="Select All" />
      </Box>
      <DataGrid
        sx={{
          borderLeft: 'none',
          borderRight: 'none',
          borderRadius: '0',
          color: theme.palette.color50555b.main,
          [`& .${gridClasses.cell}`]: {
            py: 1
          }
        }}
        rows={unassignedTestResults}
        getRowId={row => row.test_result_id}
        columns={[
          {
            field: 'test_result',
            headerName: '',
            width: 50,
            sortable: false,
            renderCell: (params) => {
              const status = params.row.test_result === 'pass' ? ComplianceStatus.Passed : ComplianceStatus.Failed;

              return (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
                  <CompliancePlip message={params.row.test_result ?? ''} status={status} />
                </Box>
              );
            }
          },
          {
            field: 'result_description',
            headerName: 'Result',
            flex: 1,
            minWidth: 450,
            renderCell: (params) => <Box>{params.row.result_description ?? params.row.test_result}</Box>
          },
          {
            field: 'test_number',
            headerName: 'Test Number',
            flex: 1,
            minWidth: 125,
            renderCell: (params) => <Box>{params.row.test_number ?? ''}</Box>
          },
          {
            field: 'sample_date',
            headerName: 'Test Date',
            flex: 1,
            minWidth: 125,
            renderCell: (params) => standardDateFormat(params.row.sample_date ?? '')
          },
          {
            field: 'received_date',
            headerName: 'Received Date',
            flex: 1,
            minWidth: 125,
            renderCell: (params) => standardDateFormat(params.row.received_date ?? '')
          },
          {
            field: 'test_date',
            headerName: 'Result Date',
            flex: 1,
            minWidth: 125,
            renderCell: (params) => standardDateFormat(params.row.test_date ?? '')
          },
          {
            field: 'test_operator',
            headerName: 'Operator',
            flex: 1,
            minWidth: 125,
            renderCell: (params) => <Box sx={{ display: 'flex', alignItems: 'center' }}>{params.row.test_operator ?? 'Unknown'}</Box>
          },
          {
            field: 'note',
            headerName: 'Assign',
            flex: 1,
            minWidth: 125,
            sortable: false,
            renderCell: (params) => (
              <Box sx={{ display: 'flex' }}>
                <Checkbox
                  onChange={() => handleToggle(Number(params.row.test_result_id))}
                  checked={selectedSporetestIds.has(Number(params.row.test_result_id))}
                />
              </Box>
            )
          },
          {
            field: 'test_result_id',
            headerName: 'Edit',
            width: 50,
            sortable: false,
            renderCell: (params) => (
              <Box onClick={() => handleOpenSporeTestModal(params.row.test_type, params.row)} sx={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }}>
                {(params.row.test_type === 'inoffice' || params.row.test_type === 'mesa') && <EditIcon fontSize="small" />}
              </Box>
            )
          }
        ]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10
            }
          },
          sorting: {
            sortModel: [
              {
                field: `test_date`,
                sort: 'desc'
              }
            ]
          }
        }}
        pageSizeOptions={[5, 10, 20, 25, 50, 100]}
        density="compact"
        getRowHeight={() => 'auto'}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
        <Button onClick={handleSubmit} variant="contained" disabled={selectedAutoclave === '' || selectedSporetestIds.size === 0 || loading}>
          {loading ? 'Submitting...' : 'Submit'}
        </Button>
      </Box>
    </>
  );
};

export default UnassignedSporetest;
